/**
 * B40 — THE PRICE AS AN ODOMETER.
 *
 * A round prices the board in one event, and until now the figure simply
 * appeared. It now counts into place: each DIGIT is its own reel, each reel
 * carries one full revolution, and the reels land left to right a frame or two
 * apart. Mechanical and quick — a ~0.4 s roll, not a float.
 *
 * Two promises this module exists to keep, because both are invisible in the
 * component and would be silently lost in a refactor:
 *
 *  1. THE PRICE NEVER STOPS BEING READABLE. The reel's RESTING position is the
 *     real digit; the animation only ever starts one revolution behind it and
 *     lands there. So a viewer with reduced motion, a browser that refuses the
 *     animation, and the frame after the roll all show the same number — there
 *     is no state in which the figure is mid-word or blank.
 *
 *  2. ONLY DIGITS ROLL. `formatMultiplierText` emits digits, a decimal point and
 *     a trailing `x`; the separators keep their own place so the figure's width
 *     never changes and the decimal never slides.
 *
 * The reel is ONE text node of `LINES_PER_REEL * 2` lines rather than twenty
 * elements: the board draws up to eleven tiles and a five-character price, so an
 * element per line would be several hundred nodes for a tenth of a second of
 * motion.
 */

/** Digits on one revolution of a reel. */
export const LINES_PER_REEL = 10

/** Between one digit's roll and the next, ms. Left to right. */
export const PRICE_ROLL_STEP_MS = 40

/** One digit's travel, ms. */
export const PRICE_ROLL_DURATION_MS = 420

/**
 * Longest a roll can take end to end, ms — the last digit's delay plus its own
 * travel, with room for a price longer than any the paytable prints today.
 */
export const PRICE_ROLL_TOTAL_MS = PRICE_ROLL_DURATION_MS + PRICE_ROLL_STEP_MS * 8

export interface PriceDigitPart {
  kind: 'digit'
  /** 0-9 — where the reel comes to rest. */
  digit: number
}

export interface PriceTextPart {
  kind: 'text'
  /** A run of everything that is not a digit: a decimal point, the trailing x. */
  text: string
}

export type PricePart = PriceDigitPart | PriceTextPart

const ZERO = '0'.charCodeAt(0)
const NINE = '9'.charCodeAt(0)

function isDigit(character: string): boolean {
  const code = character.charCodeAt(0)
  return code >= ZERO && code <= NINE
}

/**
 * The price, split into reels and the fixed runs between them.
 *
 * Non-digits are COALESCED into runs so `.50x` is two parts, not three — one
 * element per separator would be one more box for the flex row to align.
 */
export function splitPriceParts(text: string): PricePart[] {
  const out: PricePart[] = []
  let run = ''
  for (let i = 0; i < text.length; i++) {
    const character = text.charAt(i)
    if (isDigit(character)) {
      if (run.length > 0) {
        out.push({ kind: 'text', text: run })
        run = ''
      }
      out.push({ kind: 'digit', digit: character.charCodeAt(0) - ZERO })
    } else {
      run += character
    }
  }
  if (run.length > 0) out.push({ kind: 'text', text: run })
  return out
}

/**
 * The reel's own text: 0-9 twice, one digit per line.
 *
 * TWICE is what makes every digit travel the SAME distance. A reel resting on
 * the second copy of its digit can start one full revolution above it — on the
 * first copy of the same digit — so a 1 and a 9 both roll ten lines and the
 * figure settles as one object. With a single copy a 1 would twitch and a 9
 * would sweep.
 */
export function reelText(): string {
  const lines: string[] = []
  for (let copy = 0; copy < 2; copy++) {
    for (let digit = 0; digit < LINES_PER_REEL; digit++) lines.push(String(digit))
  }
  return lines.join('\n')
}

/** Where the reel RESTS, in lines from the top: the second copy of its digit. */
export function restLine(digit: number): number {
  return LINES_PER_REEL + digit
}

/** How many digits precede this part — the reel's place in the stagger. */
export function digitRanks(parts: readonly PricePart[]): number[] {
  const out: number[] = []
  let seen = 0
  for (const part of parts) {
    if (part.kind === 'digit') {
      out.push(seen)
      seen++
    } else {
      out.push(-1)
    }
  }
  return out
}

/**
 * What the price should be DRAWN as.
 *
 * The reduced-motion branch is here, as data, rather than inside the component:
 * the widget's jest environment has no DOM, so a branch that only exists inside
 * a render can never be asserted — and this is the branch that decides whether a
 * viewer who asked for no motion gets a twenty-line strip behind a clipping
 * window or simply the number. It is the one behaviour of this module a reader
 * would most want a test for.
 */
export type PriceLayout =
  | { kind: 'plain'; text: string }
  | { kind: 'reels'; parts: PricePart[] }

export function priceLayout(text: string, still: boolean): PriceLayout {
  if (still) return { kind: 'plain', text }
  return { kind: 'reels', parts: splitPriceParts(text) }
}
